import React, { useState, useEffect, useCallback } from 'react';
import { 
  Upload, Play, CheckCircle, Clock, FileVideo, ArrowLeft, Image as ImageIcon,
  Film, Tv, Sparkles, Calendar, Globe, User, Tags, Plus, X, AlertTriangle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { isCreator } from '../../utils/roleUtils';
import { 
  getContentTypes, 
  getAIModels, 
  getGenres, 
  getLanguages, 
  getSeries,
  getSeasons,
  generateUUIDFromFirebaseUID
} from '../../services/supabaseService';
import { createUploadUrl as workerCreateUploadUrl, checkVideoStatus } from '../../services/uploadService';
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = 'https://xjhktmconhidqpatxflw.supabase.co';
const supabaseAnonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InhqaGt0bWNvbmhpZHFwYXR4Zmx3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzQwMTc5OTgsImV4cCI6MjA4OTU5Mzk5OH0.yCv9MR-oEke-Y25k9UONEMRKQLe9lSMUY2tjOaqWfl8';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

const AddVideoPageExtended = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { toast } = useToast();

  // États du formulaire étendu
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content_type_id: '',
    ai_model_id: '',
    release_year: new Date().getFullYear(),
    language_id: '', // Changé pour utiliser l'ID au lieu du code
    created_by: '',
    genre_ids: [],
    poster_url: '',
    backdrop_url: '',
    // Série/épisodes
    series_id: '',
    series_title: '',
    season_number: 1,
    episode_number: 1
  });

  // Données de référence
  const [contentTypes, setContentTypes] = useState([]);
  const [aiModels, setAiModels] = useState([]);
  const [genres, setGenres] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [series, setSeries] = useState([]);
  const [seasons, setSeasons] = useState([]);
  
  // États d'upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadState, setUploadState] = useState({
    video: {
      uploading: false,
      processing: false,
      ready: false,
      error: null,
      cloudflare_id: null,
      upload_url: null,
      database_id: null
    },
    images: {
      poster: { uploading: false, url: null },
      backdrop: { uploading: false, url: null }
    }
  });

  // Chargement des données de référence
  useEffect(() => {
    loadReferenceData();
  }, []);

  const loadReferenceData = async () => {
    try {
      console.log('🔄 Chargement des données de référence...');
      
      const [typesRes, modelsRes, genresRes, seriesRes, languagesRes] = await Promise.all([
        getContentTypes(),
        getAIModels(),
        getGenres(),
        getSeries(user?.uid),
        getLanguages()
      ]);

      console.log('📦 Réponses reçues:', {
        types: typesRes,
        models: modelsRes,
        genres: genresRes,
        languages: languagesRes
      });

      // Utiliser .data directement car les fonctions retournent { success, data }
      const types = typesRes.success ? typesRes.data : [];
      const models = modelsRes.success ? modelsRes.data : [];
      const genres = genresRes.success ? genresRes.data : [];
      const seriesData = seriesRes.success ? seriesRes.data : [];
      const languages = languagesRes.success ? languagesRes.data : [];

      console.log('✅ Données chargées:', {
        types: types.length,
        models: models.length,
        genres: genres.length,
        languages: languages.length
      });

      setContentTypes(types);
      setAiModels(models);
      setGenres(genres);
      setSeries(seriesData);
      setLanguages(languages);

      // Auto-sélectionner le premier élément si les listes ne sont pas vides
      if (languages.length > 0 && !formData.language_id) {
        setFormData(prev => ({ ...prev, language_id: languages[0].id }));
      }
      if (models.length > 0 && !formData.ai_model_id) {
        setFormData(prev => ({ ...prev, ai_model_id: models[0].id }));
      }

    } catch (error) {
      console.error('❌ Erreur chargement données:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données de référence",
        variant: "destructive"
      });
    }
  };

  // Gestion des changements de type de contenu
  const handleContentTypeChange = (typeId) => {
    setFormData(prev => ({
      ...prev,
      content_type_id: typeId,
      series_id: '',
      series_title: '',
      season_number: 1,
      episode_number: 1
    }));
    setSeasons([]);
    
    const contentType = contentTypes.find(t => t.id === typeId);
    const isEpisodic = contentType?.slug && ['series', 'anime', 'manga'].includes(contentType.slug);
    
    if (!isEpisodic) {
      setFormData(prev => ({ ...prev, series_id: '', series_title: '' }));
    }
  };

  // Gestion des séries
  const handleSeriesChange = async (seriesId) => {
    setFormData(prev => ({ ...prev, series_id: seriesId, series_title: '' }));
    
    if (seriesId) {
      try {
        const response = await getSeasons(seriesId);
        setSeasons(response.data || []);
      } catch (error) {
        console.error('Erreur chargement saisons:', error);
      }
    } else {
      setSeasons([]);
    }
  };

  // Upload d'images - Version simplifiée utilisant un fallback local
  const handleImageUpload = async (file, imageType) => {
    if (!file) return null;

    setUploadState(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [imageType]: { uploading: true, url: null }
      }
    }));

    try {
      // Utiliser FileReader pour créer une URL locale temporaire
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const imageUrl = e.target.result;
          
          setUploadState(prev => ({
            ...prev,
            images: {
              ...prev.images,
              [imageType]: { uploading: false, url: imageUrl }
            }
          }));

          setFormData(prev => ({
            ...prev,
            [`${imageType}_url`]: imageUrl
          }));

          toast({
            title: "Image sélectionnée",
            description: `${imageType === 'poster' ? 'Miniature' : 'Bannière'} prête`
          });

          resolve(imageUrl);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error(`Erreur upload ${imageType}:`, error);
      
      // Solution de fallback : utiliser une URL data locale temporairement
      if (file) {
        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const fallbackUrl = e.target.result;
            
            setUploadState(prev => ({
              ...prev,
              images: {
                ...prev.images,
                [imageType]: { uploading: false, url: fallbackUrl }
              }
            }));

            setFormData(prev => ({
              ...prev,
              [`${imageType}_url`]: fallbackUrl
            }));
            
            toast({
              title: "Image chargée localement",
              description: `${imageType === 'poster' ? 'Miniature' : 'Bannière'} en mode prévisualisation`,
              variant: "default"
            });
          };
          reader.readAsDataURL(file);
          return;
        } catch (fallbackError) {
          console.error('Erreur fallback:', fallbackError);
        }
      }
      
      toast({
        title: "Erreur upload",
        description: error.response?.data?.detail || "Impossible d'uploader l'image",
        variant: "destructive"
      });
    }

    setUploadState(prev => ({
      ...prev,
      images: {
        ...prev.images,
        [imageType]: { uploading: false, url: null }
      }
    }));
    return null;
  };

  // Upload vidéo étendu - utilise le Worker Cloudflare
  const handleExtendedVideoUpload = async () => {
    if (!selectedFile || !formData.content_type_id || !formData.language_id || !formData.ai_model_id) {
      toast({
        title: "Champs requis",
        description: "Veuillez sélectionner une vidéo, type de contenu, langue et modèle IA",
        variant: "destructive"
      });
      return;
    }

    try {
      setUploadState(prev => ({
        ...prev,
        video: { ...prev.video, uploading: true, error: null }
      }));

      // 1. Créer l'URL d'upload via le Worker Cloudflare
      const workerResponse = await workerCreateUploadUrl({
        title: formData.title || selectedFile.name,
        maxDurationSeconds: 1800
      });

      if (!workerResponse.success) {
        throw new Error(workerResponse.error || 'Erreur création URL upload');
      }

      const { uploadURL, videoId } = workerResponse;

      // 2. Créer l'entrée dans Supabase
      const supabaseUUID = generateUUIDFromFirebaseUID(user.uid);
      const contentType = contentTypes.find(t => t.id === formData.content_type_id);
      
      const videoData = {
        id: crypto.randomUUID(),
        user_id: supabaseUUID,
        title: formData.title || selectedFile.name,
        description: formData.description || '',
        cloudflare_video_id: videoId,
        content_type_id: formData.content_type_id,
        ai_model_id: formData.ai_model_id,
        language_id: formData.language_id,
        release_year: formData.release_year,
        created_by: formData.created_by || user.displayName || '',
        poster_url: formData.poster_url || '',
        backdrop_url: formData.backdrop_url || '',
        playback_url: '', // Sera mis à jour après le traitement Cloudflare
        thumbnail_url: '',
        status: 'uploading',
        is_published: false,
        created_at: new Date().toISOString()
      };

      // Ajouter les données de série si applicable
      if (contentType && ['series', 'anime', 'manga'].includes(contentType.slug)) {
        if (formData.series_id) {
          videoData.series_id = formData.series_id;
        }
        videoData.season_number = formData.season_number;
        videoData.episode_number = formData.episode_number;
      }

      const { data: dbData, error: dbError } = await supabase
        .from('videos')
        .insert([videoData])
        .select()
        .single();

      if (dbError) {
        console.error('Erreur création Supabase:', dbError);
      }

      const databaseId = dbData?.id || videoId;

      setUploadState(prev => ({
        ...prev,
        video: {
          ...prev.video,
          upload_url: uploadURL,
          cloudflare_id: videoId,
          database_id: databaseId
        }
      }));

      // 3. Upload vers Cloudflare Stream
      await uploadToCloudflare(uploadURL);
      
      toast({
        title: "Upload terminé ! 🎉",
        description: "Votre contenu est prêt à être publié"
      });

    } catch (error) {
      console.error('Erreur upload étendu:', error);
      setUploadState(prev => ({
        ...prev,
        video: { ...prev.video, uploading: false, error: error.message }
      }));
      
      toast({
        title: "Erreur upload",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const uploadToCloudflare = (uploadUrl) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded * 100) / event.total);
          setUploadState(prev => ({
            ...prev,
            video: { ...prev.video, uploadProgress: progress }
          }));
        }
      });

      xhr.onload = () => {
        if (xhr.status === 200) {
          setUploadState(prev => ({
            ...prev,
            video: {
              ...prev.video,
              uploading: false,
              processing: false,
              ready: true
            }
          }));
          resolve();
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      };

      xhr.onerror = () => reject(new Error('Network error'));
      xhr.open('POST', uploadUrl);
      xhr.send(formData);
    });
  };

  // Gestion des genres (multi-select)
  const toggleGenre = (genreId) => {
    setFormData(prev => ({
      ...prev,
      genre_ids: prev.genre_ids.includes(genreId)
        ? prev.genre_ids.filter(id => id !== genreId)
        : [...prev.genre_ids, genreId]
    }));
  };

  const selectedContentType = contentTypes.find(t => t.id === formData.content_type_id);
  const isEpisodic = selectedContentType?.slug && ['series', 'anime', 'manga'].includes(selectedContentType.slug);
  const selectedGenres = genres.filter(g => formData.genre_ids.includes(g.id));

  if (!user || !isCreator(userProfile?.role)) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Accès créateur requis</h1>
          <p className="text-gray-400 mb-6">Vous devez être créateur pour accéder à cette page</p>
          <Button onClick={() => navigate('/profile')}>
            Devenir créateur
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={() => navigate('/profile')}
            size="sm"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Retour</span>
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center space-x-3">
              <Sparkles className="h-8 w-8 text-pink-400" />
              <span>Nouveau contenu IA</span>
            </h1>
            <p className="text-gray-400 mt-1">Créez votre film, série, anime ou manga IA</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Colonne gauche - Formulaire */}
          <div className="space-y-6">
            {/* Informations de base */}
            <div className="bg-gradient-to-r from-gray-900/80 to-gray-800/80 backdrop-blur-lg border border-gray-700 rounded-2xl p-6">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Film className="h-5 w-5 mr-2 text-blue-400" />
                Informations de base
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                {/* Type de contenu */}
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block">Type de contenu *</Label>
                  <select
                    value={formData.content_type_id}
                    onChange={(e) => handleContentTypeChange(e.target.value)}
                    className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Sélectionner un type</option>
                    {contentTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.label} - {type.description}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Titre */}
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block">Titre *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder={isEpisodic ? "Titre de l'épisode" : "Titre du contenu"}
                    className="bg-gray-800/50 border-gray-600 text-white"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block">Description *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder={isEpisodic ? "Synopsis de l'épisode" : "Synopsis du contenu"}
                    className="bg-gray-800/50 border-gray-600 text-white"
                    rows={3}
                  />
                </div>

                {/* Année et langue */}
                <div>
                  <Label className="text-white mb-2 block">Année de sortie</Label>
                  <Input
                    type="number"
                    value={formData.release_year}
                    onChange={(e) => setFormData(prev => ({ ...prev, release_year: parseInt(e.target.value) || '' }))}
                    min="1900"
                    max={new Date().getFullYear() + 5}
                    className="bg-gray-800/50 border-gray-600 text-white"
                  />
                </div>

                <div>
                  <Label className="text-white mb-2 block">Langue *</Label>
                  <select
                    value={formData.language_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, language_id: e.target.value }))}
                    className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  >
                    <option value="">Sélectionner une langue</option>
                    {languages.map(lang => (
                      <option key={lang.id} value={lang.id}>
                        {lang.native_name} ({lang.name})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Créé par */}
                <div className="md:col-span-2">
                  <Label className="text-white mb-2 block">Créé par</Label>
                  <Input
                    value={formData.created_by}
                    onChange={(e) => setFormData(prev => ({ ...prev, created_by: e.target.value }))}
                    placeholder="Studio, créateur, ou équipe"
                    className="bg-gray-800/50 border-gray-600 text-white"
                  />
                </div>
              </div>
            </div>

            {/* Modèle IA */}
            <div className="bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Sparkles className="h-5 w-5 mr-2 text-purple-400" />
                Modèle IA utilisé *
              </h3>
              
              <select
                value={formData.ai_model_id}
                onChange={(e) => setFormData(prev => ({ ...prev, ai_model_id: e.target.value }))}
                className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white"
              >
                <option value="">Sélectionner un modèle IA</option>
                {aiModels.map(model => (
                  <option key={model.id} value={model.id}>
                    {model.name} {model.version && `v${model.version}`}
                    {model.description && ` - ${model.description}`}
                  </option>
                ))}
              </select>
              
              {formData.ai_model_id && (
                <div className="mt-3 p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
                  <p className="text-purple-200 text-sm">
                    ✨ Modèle sélectionné: {aiModels.find(m => m.id === formData.ai_model_id)?.name}
                  </p>
                </div>
              )}
            </div>

            {/* Genres */}
            <div className="bg-gradient-to-r from-blue-900/20 to-cyan-900/20 border border-blue-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Tags className="h-5 w-5 mr-2 text-blue-400" />
                Genres ({selectedGenres.length} sélectionnés)
              </h3>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {genres.map(genre => (
                  <button
                    key={genre.id}
                    onClick={() => toggleGenre(genre.id)}
                    className={`px-3 py-2 rounded-lg text-sm transition-all ${
                      formData.genre_ids.includes(genre.id)
                        ? 'bg-blue-600 text-white border-2 border-blue-400'
                        : 'bg-gray-800 text-gray-300 border border-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    {genre.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Gestion série/épisodes */}
            {isEpisodic && (
              <div className="bg-gradient-to-r from-green-900/20 to-teal-900/20 border border-green-500/20 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                  <Tv className="h-5 w-5 mr-2 text-green-400" />
                  Gestion {selectedContentType?.label.toLowerCase()}
                </h3>
                
                <div className="space-y-4">
                  {/* Série existante ou nouvelle */}
                  <div>
                    <Label className="text-white mb-2 block">Série existante</Label>
                    <select
                      value={formData.series_id}
                      onChange={(e) => handleSeriesChange(e.target.value)}
                      className="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    >
                      <option value="">Nouvelle série</option>
                      {series.map(s => (
                        <option key={s.id} value={s.id}>{s.title}</option>
                      ))}
                    </select>
                  </div>

                  {/* Nouvelle série */}
                  {!formData.series_id && (
                    <div>
                      <Label className="text-white mb-2 block">Titre de la nouvelle série</Label>
                      <Input
                        value={formData.series_title}
                        onChange={(e) => setFormData(prev => ({ ...prev, series_title: e.target.value }))}
                        placeholder="Nom de la série"
                        className="bg-gray-800/50 border-gray-600 text-white"
                      />
                    </div>
                  )}

                  {/* Saison et épisode */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white mb-2 block">Saison</Label>
                      <Input
                        type="number"
                        value={formData.season_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, season_number: parseInt(e.target.value) || 1 }))}
                        min="1"
                        className="bg-gray-800/50 border-gray-600 text-white"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-white mb-2 block">Épisode</Label>
                      <Input
                        type="number"
                        value={formData.episode_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, episode_number: parseInt(e.target.value) || 1 }))}
                        min="1"
                        className="bg-gray-800/50 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Colonne droite - Upload et images */}
          <div className="space-y-6">
            {/* Upload vidéo */}
            <div className="bg-gradient-to-r from-pink-900/20 to-red-900/20 border border-pink-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Upload className="h-5 w-5 mr-2 text-pink-400" />
                Fichier vidéo
              </h3>

              {/* Affichage des erreurs d'upload */}
              {uploadState.video.error && (
                <div className="mb-4 p-4 bg-red-900/20 border border-red-500 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <AlertTriangle className="h-5 w-5 text-red-400" />
                    <span className="text-red-400 font-medium">Erreur d'upload</span>
                  </div>
                  <p className="text-red-300 text-sm">{uploadState.video.error}</p>
                </div>
              )}

              {/* Zone de drop */}
              <div 
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                  selectedFile 
                    ? 'border-green-500 bg-green-500/5' 
                    : 'border-gray-600 hover:border-pink-500 hover:bg-pink-500/5'
                }`}
                onClick={() => document.getElementById('video-input').click()}
              >
                <input
                  id="video-input"
                  type="file"
                  accept=".mp4,.mov,.webm"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                />

                {uploadState.video.processing ? (
                  <div className="space-y-4">
                    <Clock className="h-12 w-12 text-yellow-400 mx-auto animate-spin" />
                    <div>
                      <p className="text-yellow-300">Traitement par Cloudflare Stream...</p>
                      {uploadState.video.uploadProgress && (
                        <div className="mt-2">
                          <div className="bg-gray-700 rounded-full h-2">
                            <div 
                              className="bg-yellow-400 h-2 rounded-full transition-all duration-300"
                              style={{ width: `${uploadState.video.uploadProgress}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">{uploadState.video.uploadProgress}%</p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : uploadState.video.ready ? (
                  <div className="space-y-4">
                    <CheckCircle className="h-12 w-12 text-green-400 mx-auto" />
                    <p className="text-green-400 font-medium">Vidéo prête au streaming !</p>
                    <p className="text-gray-400 text-sm">ID: {uploadState.video.cloudflare_id}</p>
                  </div>
                ) : selectedFile ? (
                  <div className="space-y-4">
                    <FileVideo className="h-12 w-12 text-green-400 mx-auto" />
                    <div>
                      <p className="text-green-400 font-medium">{selectedFile.name}</p>
                      <p className="text-gray-400 text-sm">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                    <div>
                      <p className="text-white font-medium">Glissez-déposez votre vidéo IA</p>
                      <p className="text-gray-400 text-sm mt-1">ou cliquez pour parcourir</p>
                      <p className="text-xs text-gray-500 mt-2">MP4, MOV, WebM - Max 1GB - 30min</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Bouton d'upload - MAINTENANT EN DEHORS DE LA DROPZONE */}
              {selectedFile && !uploadState.video.processing && !uploadState.video.ready && (
                <div className="mt-4">
                  <Button
                    onClick={(e) => {
                      e.stopPropagation(); // Empêche la propagation vers la dropzone
                      handleExtendedVideoUpload();
                    }}
                    disabled={!formData.title || !formData.description || !formData.content_type_id || !formData.language_id || !formData.ai_model_id || uploadState.video.uploading}
                    className="w-full bg-pink-600 hover:bg-pink-700"
                  >
                    {uploadState.video.uploading ? (
                      <><Clock className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" />Uploader vers Cloudflare</>
                    )}
                  </Button>
                </div>
              )}
            </div>

            {/* Upload images */}
            <div className="bg-gradient-to-r from-indigo-900/20 to-purple-900/20 border border-indigo-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <ImageIcon className="h-5 w-5 mr-2 text-indigo-400" />
                Images (Cloudflare Images)
              </h3>

              <div className="space-y-4">
                {/* Miniature (poster) */}
                <div>
                  <Label className="text-white mb-2 block">Miniature verticale (poster) *</Label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'poster')}
                      className="hidden"
                      id="poster-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('poster-input').click()}
                      disabled={uploadState.images.poster.uploading}
                    >
                      {uploadState.images.poster.uploading ? (
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ImageIcon className="h-4 w-4 mr-2" />
                      )}
                      {uploadState.images.poster.url ? 'Changer' : 'Uploader'}
                    </Button>
                    {uploadState.images.poster.url && (
                      <div className="flex items-center text-green-400 text-sm">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Miniature uploadée
                      </div>
                    )}
                  </div>
                  {uploadState.images.poster.url && (
                    <div className="mt-2">
                      <img 
                        src={uploadState.images.poster.url} 
                        alt="Miniature" 
                        className="w-20 h-28 object-cover rounded-lg border border-gray-600"
                        onError={(e) => {
                          console.error('Erreur chargement image poster:', uploadState.images.poster.url);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Image poster chargée avec succès')}
                      />
                    </div>
                  )}
                </div>

                {/* Bannière (backdrop) */}
                <div>
                  <Label className="text-white mb-2 block">Bannière horizontale (backdrop)</Label>
                  <div className="flex items-center space-x-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], 'backdrop')}
                      className="hidden"
                      id="backdrop-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => document.getElementById('backdrop-input').click()}
                      disabled={uploadState.images.backdrop.uploading}
                    >
                      {uploadState.images.backdrop.uploading ? (
                        <Clock className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <ImageIcon className="h-4 w-4 mr-2" />
                      )}
                      {uploadState.images.backdrop.url ? 'Changer' : 'Uploader'}
                    </Button>
                    {uploadState.images.backdrop.url && (
                      <div className="flex items-center text-green-400 text-sm">
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Bannière uploadée
                      </div>
                    )}
                  </div>
                  {uploadState.images.backdrop.url && (
                    <div className="mt-2">
                      <img 
                        src={uploadState.images.backdrop.url} 
                        alt="Bannière" 
                        className="w-full h-20 object-cover rounded-lg border border-gray-600"
                        onError={(e) => {
                          console.error('Erreur chargement image backdrop:', uploadState.images.backdrop.url);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => console.log('Image backdrop chargée avec succès')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Bouton de publication */}
            <div className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 border border-green-500/20 rounded-2xl p-6">
              <Button
                onClick={async () => {
                  if (uploadState.video.database_id) {
                    try {
                      // Mettre à jour le statut dans Supabase
                      const { error } = await supabase
                        .from('videos')
                        .update({
                          is_published: true,
                          status: 'published'
                        })
                        .eq('id', uploadState.video.database_id);
                      
                      if (error) throw error;
                      
                      toast({
                        title: "Contenu publié ! 🎉",
                        description: "Votre création IA est maintenant visible et prête"
                      });
                      navigate('/my-videos');
                    } catch (error) {
                      console.error('Erreur publication détaillée:', error);
                      toast({
                        title: "Erreur de publication",
                        description: error.message || "Impossible de publier la vidéo",
                        variant: "destructive"
                      });
                    }
                  }
                }}
                disabled={!uploadState.video.ready || !formData.poster_url}
                className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle className="h-5 w-5 mr-2" />
                {uploadState.video.ready && formData.poster_url ? 'Publier le contenu' : 'Terminer l\'upload d\'abord'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddVideoPageExtended;